<script lang="ts">
  import { Dialog } from 'bits-ui';
  import { fade, fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import NotifyMe from '$components/notify-me.svelte';

  import Icon from '$components/ui/icon/icon.svelte';
  import Button from '$components/ui/button.svelte';
  import RadioButton from '$components/ui/radio-button.svelte';

  import { t } from '$lib/i18n';
  import { addToCart } from '$lib/cart';
  import {
    DEFAULT_TITLE,
    DEFAULT_VENDOR,
    GUEST,
    PRE_ORDER_ATTRIBUTE,
    ProductMetafields,
    ProductTags
  } from '$lib/constants';
  import { getVariantAlertId } from '$components/pdp/alerts';
  import { AddToCartLocations, elevarAnalyticsEvent, ElevarAnalyticsEvents } from '$lib/analytics';
  import { cart, getProductCategory, lastAddedItemLocation } from '$lib/cart/cart';
  import { createBreakpointMediaQueryStore } from '$lib/stores/media';

  import type { ProductCardFragment, QuickAddToCartFragment } from '$gql/storefront/operations';
  import { reducedMotion } from '$lib/stores/reduced-motion';

  import Portal from 'svelte-portal';
  import AddToCartDesktop from './add-to-cart-desktop.svelte';
  import { getFullURL } from '$lib/utils/paths';
  import { region } from '$lib/region';
  import { lang } from '$lib/lang';
  import { trimId, trimVariantId } from '$lib/utils/shopify/productsId';
  import { categoryCampaigns, tieredCampaign } from '$lib/stores/campaigns';
  import ShopifyPlans from '$components/pdp/variants/shopify-plans.svelte';
  import { format } from 'date-fns';

  export let product: ProductCardFragment | QuickAddToCartFragment;
  export let colorSelectorSelectedVariant: string | undefined = undefined;
  let className: string | undefined = undefined;
  export { className as class };
  export let targetId: string | undefined = undefined;
  export let location: AddToCartLocations | undefined = undefined;

  const resetSelected = () => {
    const result: Record<string, string> = {};

    if (product.variants.nodes.length > 1) {
      // Checks the index of the first variant that is available for sale
      const variantAvailable = product.variants.nodes.find((variant) => variant.availableForSale);
      const availableOptions = variantAvailable?.title.split(' / ') || [];

      if (variantAvailable) {
        product.options?.forEach((option, i) => {
          result[option.name] = availableOptions[i];
        });
      } else {
        // selects the first option if there's not any available
        product.options?.forEach((option) => {
          result[option.name] = option.values[0];
        });
      }
    } else {
      // selects the first option if variant is just one
      product.options?.forEach((option) => {
        result[option.name] = option.values[0];
      });
    }

    return result;
  };

  let selectedOptions: Record<string, string> = resetSelected();

  $: {
    if (!open) {
      selectedOptions = resetSelected();
    }
  }

  $: {
    if (colorSelectorSelectedVariant && product.options.length === 1) {
      selectedOptions = { [product.options[0].name]: colorSelectorSelectedVariant };
    }
  }

  $: isLimitedEdition = product.tags?.includes(ProductTags.LIMITED);
  $: hasOnlyDefaultVariant =
    product.options?.length === 1 && product.options[0].values.length === 1;
  $: selectedVariant = Object.values(selectedOptions).join(' / ');
  $: selectedProductVariant = product.variants.nodes.find(
    (variant) => variant.title === selectedVariant
  );

  // Pre-Order
  $: preOrderDay = product.metafields.find(
    (p) => p?.key === ProductMetafields.PRE_ORDER_DAY
  )?.value;
  $: isPreSale = !!preOrderDay;

  $: selectedProductVariantStock = selectedProductVariant?.quantityAvailable || 0;
  $: alert = selectedProductVariant
    ? getVariantAlertId(selectedProductVariant, { isPreSale: isPreSale, wontBeRestocked: false })
    : undefined;

  let loading = false;
  export let open = false;
  $: disabled = !selectedProductVariant?.availableForSale && !isPreSale;
  let showAdded = false;

  $: hasTieredCampaign = !!$tieredCampaign && !$tieredCampaign.isProductExcluded(product);

  $: hasCategoryCampaign =
    !!$categoryCampaigns &&
    $categoryCampaigns.some((campaign) => !campaign.isProductExcluded(product)) &&
    !hasTieredCampaign;

  $: categoryCampaign = $categoryCampaigns?.find(
    (campaign) => !campaign.isProductExcluded(product)
  );

  $: cartItems = $cart?.lines.nodes.filter((line) => line.quantity > 0) ?? [];

  const sellingPlans =
    product.sellingPlanGroups.edges.length > 0
      ? product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges.map((e) => e.node)
      : [];

  $: selectedPlanId = sellingPlans.length > 0 ? sellingPlans[0]?.id || '' : undefined;

  $: selectedPlanAllocation = selectedProductVariant?.sellingPlanAllocations.edges.find(
    (allocation) => allocation.node.sellingPlan.id === selectedPlanId
  )?.node;

  $: selectedPlanAllocationPriceAdjustment = selectedPlanAllocation?.priceAdjustments[0];

  const triggerElevar = () => {
    if (!selectedProductVariant) return;

    let collectionUrl = `/collections/${getProductCategory(product.title, product.productType) ?? ''}`;
    const variantId = trimVariantId(selectedProductVariant.id);
    const variantTitle =
      selectedProductVariant.title === DEFAULT_TITLE || selectedProductVariant.title === 'Title'
        ? product.title
        : selectedProductVariant.title;

    const newPrice = selectedPlanAllocation
      ? +selectedPlanAllocationPriceAdjustment?.price.amount
      : selectedProductVariant.compareAtPrice?.amount || selectedProductVariant.price.amount;

    const compareAtPrice = selectedPlanAllocation
      ? selectedPlanAllocationPriceAdjustment?.compareAtPrice
      : +selectedProductVariant?.compareAtPrice?.amount ||
        selectedProductVariant.compareAtPrice?.amount;

    const productPrice = hasCategoryCampaign
      ? categoryCampaign?.getDiscountedPrice(+newPrice)
      : newPrice;

    const currentCartLine = cartItems.find(
      (line) => line.merchandise.id === selectedProductVariant.id
    );

    const productQuantity = (currentCartLine?.quantity ?? 0) + 1;

    elevarAnalyticsEvent({
      event: ElevarAnalyticsEvents.ADD_TO_CART,
      user_properties: {
        visitor_type: GUEST
      },
      ecommerce: {
        currencyCode: selectedProductVariant.price.currencyCode,
        add: {
          actionField: {
            list: collectionUrl
          },
          products: [
            {
              id: selectedProductVariant?.sku,
              name: product.title,
              brand: product.vendor || DEFAULT_VENDOR,
              category: product?.productType as string,
              variant: variantTitle,
              price: `${productPrice}`,
              list: collectionUrl,
              product_id: trimId(product.id),
              quantity: `${productQuantity}`,
              variant_id: variantId,
              compare_at_price: `${compareAtPrice}` || '0.0',
              image: selectedProductVariant?.image?.url,
              url: getFullURL({
                path: `/products/${product.handle}`,
                region: $region,
                lang: $lang,
                searchQuery: `variant=${variantId}`
              })
            }
          ]
        }
      }
    });
  };

  const handleAddToCart = () => {
    if (!selectedProductVariant) return;

    showAdded = true;
    setTimeout(() => (showAdded = false), 500);

    lastAddedItemLocation.set(location || AddToCartLocations.PRODUCT_CARD);

    $addToCart([
      {
        merchandiseId: selectedProductVariant.id,
        quantity: 1,
        sellingPlanId: selectedPlanId,
        attributes: isPreSale
          ? [{ key: PRE_ORDER_ATTRIBUTE, value: preSaleDayFormatted ?? '' }]
          : []
      }
    ]).finally(() => {
      open = false;
    });

    triggerElevar();
  };

  const desktop = createBreakpointMediaQueryStore('lg');

  $: isToNotify =
    !isPreSale &&
    ((!selectedProductVariant?.availableForSale &&
      !!(hasOnlyDefaultVariant || colorSelectorSelectedVariant)) ||
      !product.availableForSale);

  $: preSaleDay = product.metafields.find((p) => p?.key === ProductMetafields.PRE_ORDER_DAY)?.value;
  $: preSaleDayFormatted = preSaleDay ? format(new Date(preSaleDay), 'MMMM do') : undefined;
</script>

{#if isToNotify}
  <Button
    type="button"
    size="xs"
    variant="primary"
    loading={loading && !open}
    class={className}
    name="Add to cart"
    on:click={() => {
      open = true;
    }}
  >
    {$t('pdp.out-of-stock.notify-me')}
  </Button>
{:else}
  <Button
    type="button"
    icon="bag-simple"
    size="xs"
    variant="primary"
    name="Add to cart"
    loading={loading && !open}
    disabled={(disabled && !!(hasOnlyDefaultVariant || colorSelectorSelectedVariant)) ||
      !product.availableForSale}
    class={className}
    on:click={() => {
      if (hasOnlyDefaultVariant || colorSelectorSelectedVariant) {
        handleAddToCart();
      } else {
        open = true;
      }
    }}
  >
    {isPreSale ? $t('pdp.pre-sale') : showAdded ? $t('products.added') : $t('products.add-to-cart')}
  </Button>
{/if}

{#if (!hasOnlyDefaultVariant && !colorSelectorSelectedVariant) || isToNotify}
  {#if !$desktop}
    <Dialog.Root bind:open>
      <Dialog.Portal>
        <Dialog.Overlay
          transition={fade}
          transitionConfig={{ duration: $reducedMotion ? 0 : 300, easing: cubicOut }}
          class="fixed z-popover inset-0 bg-primary/40"
        />
        <Dialog.Content
          transition={fly}
          transitionConfig={{ y: '100%', duration: $reducedMotion ? 0 : 300, easing: cubicOut }}
          class="fixed bottom-0 left-0 right-0 z-popover bg-highlight rounded-t-md flex-1 shadow-top-inverted"
        >
          <div class="p-2">
            <div class="flex justify-between items-center py-0.5">
              <Dialog.Title class="text-md">
                {#if isToNotify}
                  {$t('pdp.out-of-stock.notify.item-out-of-stock')}
                {:else if product.options.length === 1}
                  {$t('product-card.choose-variant-var', {
                    variant: product.options[0].name.toLowerCase()
                  })}
                {:else}
                  {$t('product-card.choose-variant')}
                {/if}
              </Dialog.Title>
              <Dialog.Close
                class="flex outline-none border-none focus-visible:ring rounded-xs motion-safe:transition-shadow"
              >
                <Icon icon="cross" size="md" />
              </Dialog.Close>
            </div>
            {#if isToNotify && product?.variants?.nodes?.length == 1 && product.variants.nodes[0]}
              <NotifyMe
                variant={product.variants.nodes[0].id}
                productId={product.id}
                showWishlistButton={false}
              />
            {:else}
              <div class="mt-8 flex flex-col gap-4">
                {#each product.options as option, i}
                  <div>
                    {#if product.options.length > 1 || sellingPlans.length > 0}
                      <p class="mb-2 text-sm text-primary/40">{option.name}</p>
                    {/if}
                    <form class="flex flex-wrap gap-2">
                      {#each option.values as value}
                        {@const currentVariant = product.variants.nodes.filter(
                          (v) => v.title === value
                        )[0]}
                        {@const qnt = currentVariant?.quantityAvailable}
                        <RadioButton
                          {value}
                          variant="ghost"
                          disabled={isLimitedEdition && qnt === 0}
                          label={value}
                          id={option.name + value + 'add-to-cart'}
                          bind:group={selectedOptions[option.name]}
                        />
                      {/each}
                    </form>

                    {#if i === product.options.length - 1}
                      <p class="mt-2 text-sm/none text-primary/80 h-5">
                        {#if alert}
                          <span
                            class="flex items-center gap-1"
                            transition:fade={{ duration: $reducedMotion ? 0 : 100 }}
                          >
                            <Icon size="md" icon="alert-hexagon-bold" />
                            {$t(alert, {
                              count: selectedProductVariantStock.toString()
                            })}
                          </span>
                        {/if}
                      </p>
                    {/if}
                  </div>
                {/each}

                {#if sellingPlans.length > 0 && selectedPlanId}
                  <ShopifyPlans {product} variant="ghost" bind:selectedPlanId />
                {/if}
              </div>

              {#if disabled && selectedProductVariant}
                <NotifyMe
                  variant={selectedProductVariant.id}
                  productId={product.id}
                  showWishlistButton={false}
                />
              {:else}
                <Button
                  type="button"
                  class="w-full mt-2"
                  size="sm"
                  on:click={handleAddToCart}
                  {loading}
                  {disabled}
                  name="Add to Cart"
                >
                  {isPreSale
                    ? $t('pdp.pre-sale')
                    : showAdded
                      ? $t('products.added')
                      : $t('products.add-to-cart')}
                </Button>
              {/if}
            {/if}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  {/if}

  {#if $desktop}
    {#if targetId}
      <Portal target={targetId ? `#${targetId}` : 'body'}>
        {#if open}
          <AddToCartDesktop
            {product}
            {isToNotify}
            {handleAddToCart}
            {disabled}
            {alert}
            {selectedProductVariantStock}
            {selectedProductVariant}
            {isLimitedEdition}
            bind:selectedOptions
            bind:selectedPlanId
            {loading}
            {showAdded}
            {isPreSale}
            onClose={() => (open = false)}
          />
        {/if}
      </Portal>
    {:else if open}
      <AddToCartDesktop
        {product}
        {isToNotify}
        {handleAddToCart}
        {disabled}
        {alert}
        {selectedProductVariantStock}
        {selectedProductVariant}
        {isLimitedEdition}
        bind:selectedOptions
        bind:selectedPlanId
        {loading}
        {showAdded}
        {isPreSale}
        onClose={() => (open = false)}
      />
    {/if}
  {/if}
{/if}
